import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { mtgAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Search, Shuffle, Sparkles, X, Plus, Trash2, Upload, Library, Layers } from 'lucide-react';

const MTG_FORMATS = ['commander', 'standard', 'modern', 'legacy', 'vintage', 'pioneer', 'pauper'];

const MTGTools = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [activeTab, setActiveTab] = useState('search');
    const [searchQuery, setSearchQuery] = useState('');
    const [colorFilter, setColorFilter] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [searchType, setSearchType] = useState('cards');
    
    // Deck state
    const [decks, setDecks] = useState([]);
    const [selectedDeck, setSelectedDeck] = useState(null);
    const [showDeckModal, setShowDeckModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [deckLoading, setDeckLoading] = useState(false);
    
    // Import form
    const [importForm, setImportForm] = useState({
        name: '',
        format: 'commander',
        deck_list: '',
        commander: ''
    });

    // Card autocomplete
    const [autocompleteResults, setAutocompleteResults] = useState([]);
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const autocompleteRef = useRef(null);

    // Fetch decks
    useEffect(() => {
        const fetchDecks = async () => {
            try {
                const response = await mtgAPI.getDecks();
                setDecks(response.data || []);
            } catch (error) {
                console.error('Error fetching decks:', error);
            }
        };
        fetchDecks();
    }, []);

    // Card search
    const searchCards = useCallback(async () => {
        if (!searchQuery.trim()) return;
        
        try {
            setLoading(true);
            let response;
            
            if (searchType === 'commanders') {
                response = await mtgAPI.searchCommanders(searchQuery, colorFilter);
            } else {
                response = await mtgAPI.searchCards(searchQuery);
            }
            
            setSearchResults(response.data?.data || []);
        } catch (error) {
            console.error('Search error:', error);
            toast.error('Search failed');
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, searchType, colorFilter]);

    const handleSearch = (e) => {
        e.preventDefault();
        searchCards();
    };

    // Card autocomplete
    const handleAutocomplete = async (query) => {
        setSearchQuery(query);
        if (query.length < 2) {
            setAutocompleteResults([]);
            setShowAutocomplete(false);
            return;
        }
        
        try {
            const response = await mtgAPI.autocompleteCard(query);
            setAutocompleteResults(response.data?.data || []);
            setShowAutocomplete(true);
        } catch (error) {
            console.error('Autocomplete error:', error);
        }
    };

    const selectAutocomplete = async (cardName) => {
        setSearchQuery(cardName);
        setShowAutocomplete(false);
        
        try {
            setLoading(true);
            const response = await mtgAPI.getCardByName(cardName, true);
            if (response.data) {
                setSelectedCard(response.data);
            }
        } catch (error) {
            console.error('Error getting card:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRandomCard = async () => {
        try {
            setLoading(true);
            const response = await mtgAPI.getRandomCard();
            setSelectedCard(response.data);
        } catch (error) {
            console.error('Error getting random card:', error);
            toast.error('Failed to get random card');
        } finally {
            setLoading(false);
        }
    };

    const getRandomCommander = async () => {
        try {
            setLoading(true);
            const response = await mtgAPI.getRandomCard('is:commander');
            setSelectedCard(response.data);
        } catch (error) {
            console.error('Error getting random commander:', error);
            toast.error('Failed to get random commander');
        } finally {
            setLoading(false);
        }
    };

    // Deck import
    const handleImportDeck = async () => {
        if (!importForm.name || !importForm.deck_list.trim()) {
            toast.error('Please provide a deck name and card list');
            return;
        }
        
        try {
            setDeckLoading(true);
            const response = await mtgAPI.importDeck(importForm);
            setDecks([...decks, response.data]);
            toast.success(`Deck imported! ${response.data.total_cards} cards loaded.`);
            setShowImportModal(false);
            setImportForm({ name: '', format: 'commander', deck_list: '', commander: '' });
        } catch (error) {
            console.error('Import error:', error);
            toast.error('Failed to import deck');
        } finally {
            setDeckLoading(false);
        }
    };

    // Delete deck
    const handleDeleteDeck = async (deckId) => {
        try {
            await mtgAPI.deleteDeck(deckId);
            setDecks(decks.filter(d => d.deck_id !== deckId));
            setSelectedDeck(null);
            toast.success('Deck deleted');
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete deck');
        }
    };

    // Color indicators
    const colorSymbols = {
        W: '⚪', U: '🔵', B: '⚫', R: '🔴', G: '🟢'
    };

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="mtg-tools-page">
            <div className="scanlines" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 border-b border-[#003b00] backdrop-blur-sm">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="text-[#00ff41]"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-[#00ff41] tracking-wider flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            MTG_TOOLS
                        </h1>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20 pb-10 px-4">
                <div className="container mx-auto max-w-6xl">
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="bg-[#0a0a0a] border border-[#003b00] mb-6">
                            <TabsTrigger 
                                value="search"
                                className="data-[state=active]:bg-[#00ff41] data-[state=active]:text-black"
                            >
                                <Search className="w-4 h-4 mr-2" />
                                Card Search
                            </TabsTrigger>
                            <TabsTrigger 
                                value="decks"
                                className="data-[state=active]:bg-[#00ff41] data-[state=active]:text-black"
                            >
                                <Library className="w-4 h-4 mr-2" />
                                My Decks ({decks.length})
                            </TabsTrigger>
                        </TabsList>

                        {/* Card Search Tab */}
                        <TabsContent value="search">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="terminal-window mb-6"
                            >
                                <div className="terminal-header">
                                    <div className="terminal-dot red" />
                                    <div className="terminal-dot yellow" />
                                    <div className="terminal-dot green" />
                                    <span className="ml-4 text-[#00ff41] text-sm">scryfall_search.exe</span>
                                </div>

                                <div className="p-6">
                                    <form onSubmit={handleSearch} className="space-y-4">
                                        <div className="flex flex-wrap gap-4">
                                            <Select value={searchType} onValueChange={setSearchType}>
                                                <SelectTrigger className="w-[180px] bg-[#050505] border-[#003b00] text-[#00ff41]">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                                    <SelectItem value="cards" className="text-[#00ff41]">All Cards</SelectItem>
                                                    <SelectItem value="commanders" className="text-[#00ff41]">Commanders</SelectItem>
                                                </SelectContent>
                                            </Select>

                                            {searchType === 'commanders' && (
                                                <Select value={colorFilter} onValueChange={setColorFilter}>
                                                    <SelectTrigger className="w-[180px] bg-[#050505] border-[#003b00] text-[#00ff41]">
                                                        <SelectValue placeholder="Color Identity" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                                        <SelectItem value="" className="text-[#00ff41]">Any Color</SelectItem>
                                                        <SelectItem value="w" className="text-[#00ff41]">⚪ White</SelectItem>
                                                        <SelectItem value="u" className="text-[#00ff41]">🔵 Blue</SelectItem>
                                                        <SelectItem value="b" className="text-[#00ff41]">⚫ Black</SelectItem>
                                                        <SelectItem value="r" className="text-[#00ff41]">🔴 Red</SelectItem>
                                                        <SelectItem value="g" className="text-[#00ff41]">🟢 Green</SelectItem>
                                                        <SelectItem value="wubrg" className="text-[#00ff41]">5-Color</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        </div>

                                        <div className="relative" ref={autocompleteRef}>
                                            <div className="flex gap-2">
                                                <Input
                                                    data-testid="mtg-search-input"
                                                    value={searchQuery}
                                                    onChange={(e) => handleAutocomplete(e.target.value)}
                                                    onFocus={() => autocompleteResults.length > 0 && setShowAutocomplete(true)}
                                                    placeholder="Search cards... (e.g., 'lightning bolt', 'type:creature cmc:3')"
                                                    className="bg-[#050505] border-[#003b00] text-[#00ff41] flex-1"
                                                />
                                                <Button
                                                    data-testid="mtg-search-btn"
                                                    type="submit"
                                                    disabled={loading}
                                                    className="bg-[#00ff41] text-black"
                                                >
                                                    <Search className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            
                                            {/* Autocomplete dropdown */}
                                            {showAutocomplete && autocompleteResults.length > 0 && (
                                                <div className="absolute z-50 w-full mt-1 bg-[#0a0a0a] border border-[#003b00] max-h-48 overflow-y-auto">
                                                    {autocompleteResults.slice(0, 10).map((name) => (
                                                        <div
                                                            key={name}
                                                            onClick={() => selectAutocomplete(name)}
                                                            className="px-3 py-2 cursor-pointer hover:bg-[#003b00] text-[#00ff41] text-sm font-mono"
                                                        >
                                                            {name}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </form>

                                    <div className="flex gap-2 mt-4">
                                        <Button
                                            data-testid="random-card-btn"
                                            variant="outline"
                                            onClick={getRandomCard}
                                            disabled={loading}
                                            className="border-[#003b00] text-[#00ff41]"
                                        >
                                            <Shuffle className="w-4 h-4 mr-2" />
                                            Random Card
                                        </Button>
                                        <Button
                                            data-testid="random-commander-btn"
                                            variant="outline"
                                            onClick={getRandomCommander}
                                            disabled={loading}
                                            className="border-[#003b00] text-[#00ff41]"
                                        >
                                            <Shuffle className="w-4 h-4 mr-2" />
                                            Random Commander
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Results Grid */}
                            {loading ? (
                                <div className="flex justify-center py-20">
                                    <div className="matrix-loader" />
                                </div>
                            ) : searchResults.length > 0 ? (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
                                >
                                    {searchResults.map((card) => (
                                        <motion.div
                                            key={card.id}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            whileHover={{ scale: 1.05 }}
                                            onClick={() => setSelectedCard(card)}
                                            className="cursor-pointer mtg-card"
                                        >
                                            {card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal ? (
                                                <img
                                                    src={card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal}
                                                    alt={card.name}
                                                    className="w-full rounded-lg border-2 border-[#003b00] hover:border-[#00ff41] transition-colors"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-full aspect-[2.5/3.5] bg-[#0a0a0a] border-2 border-[#003b00] flex items-center justify-center p-4">
                                                    <span className="text-[#00ff41] text-center text-sm">{card.name}</span>
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : searchQuery && !loading ? (
                                <div className="text-center py-20">
                                    <p className="text-[#008f11] font-mono">NO_RESULTS_FOUND</p>
                                </div>
                            ) : (
                                <div className="text-center py-20">
                                    <Sparkles className="w-16 h-16 text-[#003b00] mx-auto mb-4" />
                                    <p className="text-[#008f11] font-mono">
                                        {'>'} Search for cards or get a random one
                                    </p>
                                </div>
                            )}
                        </TabsContent>

                        {/* Decks Tab */}
                        <TabsContent value="decks">
                            <div className="flex justify-end mb-4">
                                <Button
                                    data-testid="import-deck-btn"
                                    onClick={() => setShowImportModal(true)}
                                    className="bg-[#00ff41] text-black"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    Import Deck
                                </Button>
                            </div>

                            {decks.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {decks.map((deck) => (
                                        <motion.div
                                            key={deck.deck_id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="terminal-window cursor-pointer card-hover"
                                            onClick={() => {
                                                setSelectedDeck(deck);
                                                setShowDeckModal(true);
                                            }}
                                        >
                                            <div className="terminal-header">
                                                <div className="terminal-dot red" />
                                                <div className="terminal-dot yellow" />
                                                <div className="terminal-dot green" />
                                                <span className="ml-4 text-[#00ff41] text-sm truncate">
                                                    {deck.name}.deck
                                                </span>
                                            </div>

                                            <div className="p-4">
                                                <h3 className="text-lg font-bold text-[#00ff41] mb-2">
                                                    {deck.name}
                                                </h3>
                                                <div className="space-y-1 text-sm text-[#008f11]">
                                                    <p>Format: {deck.format.toUpperCase()}</p>
                                                    <p>Cards: {deck.total_cards}</p>
                                                    {deck.commander && (
                                                        <p className="truncate">Commander: {deck.commander}</p>
                                                    )}
                                                    <div className="flex gap-1 mt-2">
                                                        {deck.colors?.map(color => (
                                                            <span key={color} className="text-lg">
                                                                {colorSymbols[color]}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-20 terminal-window">
                                    <Library className="w-16 h-16 text-[#003b00] mx-auto mb-4" />
                                    <p className="text-[#008f11] font-mono mb-4">
                                        {'>'} No decks yet
                                    </p>
                                    <Button
                                        onClick={() => setShowImportModal(true)}
                                        className="bg-[#00ff41] text-black"
                                    >
                                        Import Your First Deck
                                    </Button>
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </main>

            {/* Card Detail Modal */}
            <Dialog open={!!selectedCard} onOpenChange={() => setSelectedCard(null)}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl tracking-wider">
                            {selectedCard?.name}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedCard && (
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                {selectedCard.image_uris?.large || selectedCard.card_faces?.[0]?.image_uris?.large ? (
                                    <img
                                        src={selectedCard.image_uris?.large || selectedCard.card_faces?.[0]?.image_uris?.large}
                                        alt={selectedCard.name}
                                        className="w-full rounded-lg border-2 border-[#00ff41]"
                                    />
                                ) : (
                                    <div className="w-full aspect-[2.5/3.5] bg-[#050505] border-2 border-[#00ff41] flex items-center justify-center">
                                        <span className="text-[#00ff41]">{selectedCard.name}</span>
                                    </div>
                                )}
                            </div>

                            <ScrollArea className="h-[400px]">
                                <div className="space-y-4">
                                    <div className="data-row">
                                        <p className="data-label">Mana Cost</p>
                                        <p className="data-value">{selectedCard.mana_cost || 'N/A'}</p>
                                    </div>

                                    <div className="data-row">
                                        <p className="data-label">Type</p>
                                        <p className="data-value">{selectedCard.type_line}</p>
                                    </div>

                                    {selectedCard.oracle_text && (
                                        <div className="data-row">
                                            <p className="data-label">Text</p>
                                            <p className="data-value text-sm whitespace-pre-wrap">
                                                {selectedCard.oracle_text}
                                            </p>
                                        </div>
                                    )}

                                    {selectedCard.power && (
                                        <div className="data-row">
                                            <p className="data-label">P/T</p>
                                            <p className="data-value">
                                                {selectedCard.power}/{selectedCard.toughness}
                                            </p>
                                        </div>
                                    )}

                                    <div className="data-row">
                                        <p className="data-label">Set</p>
                                        <p className="data-value">{selectedCard.set_name}</p>
                                    </div>

                                    <div className="data-row">
                                        <p className="data-label">Rarity</p>
                                        <p className="data-value capitalize">{selectedCard.rarity}</p>
                                    </div>

                                    {selectedCard.prices?.usd && (
                                        <div className="data-row">
                                            <p className="data-label">Price (USD)</p>
                                            <p className="data-value">${selectedCard.prices.usd}</p>
                                        </div>
                                    )}

                                    {selectedCard.legalities && (
                                        <div className="data-row">
                                            <p className="data-label">Legalities</p>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {Object.entries(selectedCard.legalities)
                                                    .filter(([_, status]) => status === 'legal')
                                                    .slice(0, 6)
                                                    .map(([format]) => (
                                                        <span 
                                                            key={format}
                                                            className="px-2 py-0.5 bg-[#003b00] text-[#00ff41] text-xs uppercase"
                                                        >
                                                            {format}
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Deck Import Modal */}
            <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl tracking-wider">IMPORT_DECK</DialogTitle>
                        <DialogDescription className="text-[#008f11]">
                            Paste your deck list in any common format
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Deck Name</label>
                            <Input
                                value={importForm.name}
                                onChange={(e) => setImportForm({...importForm, name: e.target.value})}
                                placeholder="My Awesome Deck"
                                className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Format</label>
                            <Select 
                                value={importForm.format} 
                                onValueChange={(v) => setImportForm({...importForm, format: v})}
                            >
                                <SelectTrigger className="bg-[#050505] border-[#003b00]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0a0a0a] border-[#003b00]">
                                    {MTG_FORMATS.map(f => (
                                        <SelectItem key={f} value={f} className="text-[#00ff41] capitalize">
                                            {f}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {importForm.format === 'commander' && (
                            <div>
                                <label className="text-sm text-[#008f11] mb-1 block">Commander (optional)</label>
                                <Input
                                    value={importForm.commander}
                                    onChange={(e) => setImportForm({...importForm, commander: e.target.value})}
                                    placeholder="Atraxa, Praetors' Voice"
                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-sm text-[#008f11] mb-1 block">Deck List</label>
                            <Textarea
                                value={importForm.deck_list}
                                onChange={(e) => setImportForm({...importForm, deck_list: e.target.value})}
                                placeholder={`Paste your deck list here, e.g.:
4 Lightning Bolt
4 Counterspell
2 Sol Ring
1 Command Tower`}
                                className="bg-[#050505] border-[#003b00] text-[#00ff41] h-48 font-mono text-sm"
                            />
                        </div>

                        <Button
                            onClick={handleImportDeck}
                            disabled={deckLoading}
                            className="w-full bg-[#00ff41] text-black"
                        >
                            {deckLoading ? (
                                <>
                                    <div className="w-4 h-4 mr-2 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Import Deck
                                </>
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Deck Detail Modal */}
            <Dialog open={showDeckModal} onOpenChange={setShowDeckModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl tracking-wider flex items-center justify-between">
                            {selectedDeck?.name}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    handleDeleteDeck(selectedDeck?.deck_id);
                                    setShowDeckModal(false);
                                }}
                                className="text-[#ff0033]"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </DialogTitle>
                    </DialogHeader>

                    {selectedDeck && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="data-row">
                                    <p className="data-label">Format</p>
                                    <p className="data-value uppercase">{selectedDeck.format}</p>
                                </div>
                                <div className="data-row">
                                    <p className="data-label">Total Cards</p>
                                    <p className="data-value">{selectedDeck.total_cards}</p>
                                </div>
                                <div className="data-row">
                                    <p className="data-label">Colors</p>
                                    <p className="data-value">
                                        {selectedDeck.colors?.map(c => colorSymbols[c]).join(' ') || 'Colorless'}
                                    </p>
                                </div>
                            </div>

                            {selectedDeck.commander && (
                                <div className="data-row">
                                    <p className="data-label">Commander</p>
                                    <p className="data-value">{selectedDeck.commander}</p>
                                </div>
                            )}

                            <div>
                                <p className="data-label mb-2">Card List</p>
                                <ScrollArea className="h-[300px] border border-[#003b00] p-3">
                                    <div className="space-y-1">
                                        {selectedDeck.cards?.map((card, idx) => (
                                            <div 
                                                key={idx}
                                                className="flex items-center justify-between py-1 border-b border-[#003b00]/50 last:border-0"
                                            >
                                                <span 
                                                    className="text-[#00ff41] cursor-pointer hover:underline"
                                                    onClick={async () => {
                                                        try {
                                                            const response = await mtgAPI.getCardByName(card.name, false);
                                                            if (response.data) {
                                                                setSelectedCard(response.data);
                                                            }
                                                        } catch (e) {
                                                            console.error(e);
                                                        }
                                                    }}
                                                >
                                                    {card.name}
                                                </span>
                                                <span className="text-[#008f11]">x{card.quantity}</span>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MTGTools;
